import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
	Paper,
	Typography,
	Box,
	TextField,
	Button,
	IconButton,
	Switch,
	FormControlLabel,
	Chip,
} from "@material-ui/core";
import { Add, Delete, AccessTime } from "@material-ui/icons";
import SaveIcon from "@material-ui/icons/Save";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
	},
	dayContainer: {
		marginBottom: theme.spacing(2.5),
		padding: theme.spacing(2.5),
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper,
		border: `2px solid ${theme.palette.divider}`,
		transition: "all 0.2s ease-in-out",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
		},
	},
	dayHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
		paddingBottom: theme.spacing(1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	dayTitle: {
		fontWeight: 600,
		fontSize: "1.15rem",
		color: theme.palette.text.primary,
	},
	alwaysActiveChip: {
		marginLeft: theme.spacing(1.5),
		backgroundColor: theme.palette.success.main,
		color: "white",
		fontWeight: 500,
	},
	rangeContainer: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.action.hover,
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		transition: "all 0.2s ease-in-out",
		"&:hover": {
			backgroundColor: theme.palette.action.selected,
			transform: "translateX(4px)",
		},
	},
	timeInput: {
		width: 150,
		"& .MuiOutlinedInput-root": {
			backgroundColor: theme.palette.background.paper,
		},
	},
	timeSeparator: {
		fontSize: "1.2rem",
		fontWeight: 500,
		color: theme.palette.text.secondary,
		margin: `0 ${theme.spacing(1)}`,
	},
	addButton: {
		marginTop: theme.spacing(1.5),
		textTransform: "none",
		fontWeight: 500,
		borderRadius: 8,
	},
	deleteButton: {
		color: theme.palette.error.main,
		"&:hover": {
			backgroundColor: `${theme.palette.error.main}15`,
		},
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(3),
		color: theme.palette.text.secondary,
		fontStyle: "italic",
		backgroundColor: theme.palette.action.hover,
		borderRadius: 8,
		border: `1px dashed ${theme.palette.divider}`,
	},
	saveButton: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(1.5, 4),
		textTransform: "none",
		fontWeight: 600,
		fontSize: "1rem",
		borderRadius: 8,
		boxShadow: theme.shadows[2],
		"&:hover": {
			boxShadow: theme.shadows[4],
		},
	},
}));

const ScheduleEditor = ({ schedule, onChange, onSave }) => {
	const classes = useStyles();
	const [localSchedule, setLocalSchedule] = useState(schedule || {});

	useEffect(() => {
		setLocalSchedule(schedule || {});
	}, [schedule]);

	const daysOfWeek = [
		{ key: "monday", label: "Lunes" },
		{ key: "tuesday", label: "Martes" },
		{ key: "wednesday", label: "Miércoles" },
		{ key: "thursday", label: "Jueves" },
		{ key: "friday", label: "Viernes" },
		{ key: "saturday", label: "Sábado" },
		{ key: "sunday", label: "Domingo" },
	];

	const handleAlwaysActiveToggle = (dayKey) => {
		const newSchedule = {
			...localSchedule,
			[dayKey]: {
				alwaysActive: !localSchedule[dayKey]?.alwaysActive,
				ranges: localSchedule[dayKey]?.alwaysActive ? localSchedule[dayKey].ranges : [],
			},
		};
		setLocalSchedule(newSchedule);
		if (onChange) onChange(newSchedule);
	};

	const handleAddRange = (dayKey) => {
		const newSchedule = {
			...localSchedule,
			[dayKey]: {
				...localSchedule[dayKey],
				alwaysActive: false,
				ranges: [
					...(localSchedule[dayKey]?.ranges || []),
					{ start: "09:00", end: "17:00" },
				],
			},
		};
		setLocalSchedule(newSchedule);
		if (onChange) onChange(newSchedule);
	};

	const handleRemoveRange = (dayKey, rangeIndex) => {
		const newSchedule = {
			...localSchedule,
			[dayKey]: {
				...localSchedule[dayKey],
				ranges: localSchedule[dayKey].ranges.filter((_, idx) => idx !== rangeIndex),
			},
		};
		setLocalSchedule(newSchedule);
		if (onChange) onChange(newSchedule);
	};

	const handleRangeChange = (dayKey, rangeIndex, field, value) => {
		const newSchedule = {
			...localSchedule,
			[dayKey]: {
				...localSchedule[dayKey],
				ranges: localSchedule[dayKey].ranges.map((range, idx) =>
					idx === rangeIndex ? { ...range, [field]: value } : range
				),
			},
		};
		setLocalSchedule(newSchedule);
		if (onChange) onChange(newSchedule);
	};

	const handleSave = () => {
		if (onSave) {
			onSave(localSchedule);
		}
	};

	return (
		<div className={classes.root}>
			{daysOfWeek.map((day) => {
				const daySchedule = localSchedule[day.key] || { alwaysActive: false, ranges: [] };
				const isAlwaysActive = daySchedule.alwaysActive;

				return (
					<Paper key={day.key} className={classes.dayContainer} elevation={0}>
						<div className={classes.dayHeader}>
							<Box display="flex" alignItems="center">
								<Typography className={classes.dayTitle}>{day.label}</Typography>
								{isAlwaysActive && (
									<Chip
										icon={<AccessTime />}
										label="Siempre Activo"
										color="primary"
										size="small"
										className={classes.alwaysActiveChip}
									/>
								)}
							</Box>
							<FormControlLabel
								control={
									<Switch
										checked={isAlwaysActive}
										onChange={() => handleAlwaysActiveToggle(day.key)}
										color="primary"
										size="small"
									/>
								}
								label="Siempre Activo"
							/>
						</div>

						{!isAlwaysActive && (
							<>
								{daySchedule.ranges && daySchedule.ranges.length > 0 ? (
									daySchedule.ranges.map((range, rangeIndex) => (
										<div key={rangeIndex} className={classes.rangeContainer}>
											<TextField
												type="time"
												label="Inicio"
												value={range.start || "09:00"}
												onChange={(e) =>
													handleRangeChange(day.key, rangeIndex, "start", e.target.value)
												}
												className={classes.timeInput}
												margin="dense"
												variant="outlined"
												InputLabelProps={{ shrink: true }}
											/>
											<Typography className={classes.timeSeparator}>
												→
											</Typography>
											<TextField
												type="time"
												label="Fin"
												value={range.end || "17:00"}
												onChange={(e) =>
													handleRangeChange(day.key, rangeIndex, "end", e.target.value)
												}
												className={classes.timeInput}
												margin="dense"
												variant="outlined"
												InputLabelProps={{ shrink: true }}
											/>
											<IconButton
												size="small"
												onClick={() => handleRemoveRange(day.key, rangeIndex)}
												className={classes.deleteButton}
											>
												<Delete />
											</IconButton>
										</div>
									))
								) : (
									<div className={classes.emptyState}>
										No hay rangos de horario configurados
									</div>
								)}

								<Button
									variant="outlined"
									size="small"
									startIcon={<Add />}
									onClick={() => handleAddRange(day.key)}
									className={classes.addButton}
									disabled={isAlwaysActive}
								>
									Agregar Rango
								</Button>
							</>
						)}
					</Paper>
				);
			})}

			<Box mt={3} display="flex" justifyContent="center">
				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					size="large"
					className={classes.saveButton}
					startIcon={<SaveIcon />}
				>
					Guardar Horario
				</Button>
			</Box>
		</div>
	);
};

export default ScheduleEditor;

